import { useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from 'Douleutaras/redux/hooks';
import { selectMe } from 'Douleutaras/redux/data/users/meSlice';
import {
  getSellerServicePrices,
  selectServicesPricing,
  ServicesPricingState
} from 'Douleutaras/redux/data/seller/servicesPricingSlice';

import { hasRenderableCatalogue } from '../ServicesCatalogue/ServicesCatalogue';
import { withoutUnpricedServices } from '../ServicesCatalogue/servicesPricingMapper';
import { ServicesCatalogueData } from '../ServicesCatalogue/types';

export interface UseSellerServicePricesResult {
  servicesPricing: ServicesPricingState;
  /** The listing as shown: without unpriced services while `unpricedServicesEnabled` is off. */
  catalogue?: ServicesCatalogueData;
  areServicePriceFlagsReady: boolean;
  showServicesCatalogue: boolean;
}

/**
 * Owns the public, seller-scoped service-price listing for the viewed pro: its fetch (kept in sync
 * with the viewed username), the catalogue it shows, and the two flags the profile derives from it.
 *
 * `showServicesCatalogue` keeps the Services tab/section visible while the listing loads or errors —
 * it hides only once we know the pro has no priced services — so the catalogue's skeleton and error
 * states stay reachable. (Otherwise a cold-load failure would silently hide the section.)
 */
export const useSellerServicePrices = (username?: string): UseSellerServicePricesResult => {
  const dispatch = useAppDispatch();
  const servicesPricing = useAppSelector(selectServicesPricing);

  const areServicePriceFlagsReady = Boolean(username)
    && servicesPricing.loaded
    && !servicesPricing.loading
    && servicesPricing.username === username;

  // Off for visitors, who carry no `user/me`
  const areUnpricedShown = Boolean(useAppSelector(selectMe)?.unpricedServicesEnabled);

  const catalogue = useMemo(
    () => areUnpricedShown || !servicesPricing.data ? servicesPricing.data : withoutUnpricedServices(servicesPricing.data),
    [servicesPricing.data, areUnpricedShown]
  );

  const showServicesCatalogue =
    hasRenderableCatalogue(catalogue) || servicesPricing.loading || servicesPricing.error;

  useEffect(() => {
    if (!username || servicesPricing.loading) {
      return;
    }
    if (servicesPricing.loaded && (servicesPricing.username ?? username) === username) {
      return;
    }
    dispatch(getSellerServicePrices({ username }));
  }, [username, servicesPricing.loaded, servicesPricing.loading, servicesPricing.username]);

  return { servicesPricing, catalogue, areServicePriceFlagsReady, showServicesCatalogue };
};
